import moment from 'moment'
import "moment-timezone"

/**
 * Class TimeManager.js
 *
 * @class TimeManager
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.11] app/src/manager/TimeManager.js
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export class TimeManager {
  constructor(serverTimezone, clientTimezone) {
    this.setServerTimezone(serverTimezone);
    this.setClientTimezone(clientTimezone);
  }

  /**
   *
   * @returns {*|string}
   */
  static getTimezoneFromClient() {
    return moment.tz.guess(true);
  }

  /**
   *
   * @returns {*|string}
   */
  getServerTimezone() {
    return this._serverTimezone;
  }

  /**
   *
   * @param serverTimezone
   * @returns {TimeManager}
   */
  setServerTimezone(serverTimezone) {
    this._serverTimezone = moment.tz.zone(serverTimezone = (serverTimezone ?? 'UTC'));

    if (!this._serverTimezone) throw new Error('Invalid Timezone: ' + serverTimezone);

    this._serverAndClientAreSameTimezone = areSameTimezone(this._serverTimezone, this._clientTimezone);

    return this;
  }

  /**
   *
   * @returns {*}
   */
  getClientTimezone() {
    return this._clientTimezone;
  }

  /**
   *
   * @param clientTimezone
   * @returns {TimeManager}
   */
  setClientTimezone(clientTimezone) {
    this._clientTimezone = moment.tz.zone(clientTimezone = (clientTimezone ?? TimeManager.getTimezoneFromClient() ?? 'Europe/Brussels'));

    if (!this._clientTimezone) throw new Error('Invalid Timezone: ' + clientTimezone);

    this._serverAndClientAreSameTimezone = areSameTimezone(this._serverTimezone, this._clientTimezone);

    return this;
  }

  /**
   *
   * @returns {boolean}
   */
  serverAndClientAreSameTimezone() {
    return this._serverAndClientAreSameTimezone;
  }

  /**
   *
   * @param data
   * @returns {string | *}
   */
  convertDataFromServerToClient(data) {
    return convertDataToRightTimezone(data, this._serverTimezone, this._clientTimezone)
  }

  /**
   *
   * @param data
   * @returns {string | *}
   */
  convertDataFromClientToServer(data) {
    return convertDataToRightTimezone(data, this._clientTimezone, this._serverTimezone)
  }

  // /**
  //  *
  //  * @returns {ApolloLink}
  //  */
  // createTimezoneLink() {
  //   return new ApolloLink((operation, forward) => {
  //     // Do nothing if client and server are on the same timezone
  //     if (this.serverAndClientAreSameTimezone())
  //       return forward(operation);
  //
  //     // Convert query parameters from client timezone to server timezone
  //     if (operation?.query?.definitions){
  //       operation.query.definitions.forEach(definition => {
  //          if (definition.variableDefinitions){
  //            definition.variableDefinitions.forEach(variableDefinition => {
  //              const variable = variableDefinition.variable;
  //
  //               if (variable.kind === 'Variable' && variable?.name?.value)
  //                 variable.name.value = convertDataToRightTimezone(variable.name.value, this._clientTimezone, this._serverTimezone);
  //            })
  //          }
  //       })
  //     }
  //
  //     // Convert given variables object from client timezone to server timezone
  //     if (operation.variables)
  //       operation.variables = convertDataToRightTimezone(operation.variables, this._clientTimezone, this._serverTimezone);
  //
  //     // Convert returned data from server to client timezone.
  //     return forward(operation).map((data) => convertDataToRightTimezone(data, this._serverTimezone, this._clientTimezone));
  //   });
  // }
}


function convertDataToRightTimezone(data, fromZone, toZone) {
  if (data) {
    // checks after string not strictly necessary, used to reduce unnecessary checks
    if (typeof data === 'string' && data.length < 20 && data.length > 4 && data.includes(":")) {

      // Convert Timezone: YYYY-MM-DD HH:mm:ss
      if (isValidFormat(data, dateTimeFormat))
        return convert(data, fromZone, toZone, dateTimeFormat);

      // Convert Timezone: HH:mm
      // if (isValidFormat(data, timeFormat1))
      //  return convert(data, fromZone, toZone, timeFormat1);

      // Convert Timezone: HH:mm:ss
      // if (isValidFormat(data, timeFormat2))
      //  return convert(data, fromZone, toZone, timeFormat2);
    }

    if (Array.isArray(data)) {
      for (let i=0; i<data.length; i++)
        data[i] = convertDataToRightTimezone(data[i], fromZone, toZone)
    } else if (isObject(data)) {
      for (const prop in data)
        if (Object.prototype.hasOwnProperty.call(data, prop))
          data[prop] = convertDataToRightTimezone(data[prop], fromZone, toZone)
    }
  }

  return data;
}

function convert(input, fromZone, toZone, format) {
  return removeZone(
    moment(input, format).tz(fromZone.name),
    moment(input, format).tz(toZone.name)
  ).format(format)
}

function isValidFormat(input, format) {
  return moment(input, format, true).isValid()
}

function areSameTimezone(timezone1, timezone2) {
  if (!(timezone1 && timezone2))
    return false;

  return timezone1.name === timezone2.name
}

function isObject(obj) {
  if (obj == null) return false;

  return typeof obj === 'object';
}

function removeZone(fromMoment, toMoment) {
  const offsetBetweenZonesInMinutes = -fromMoment.utcOffset() + toMoment.utcOffset();
  return moment.unix(fromMoment.unix() + offsetBetweenZonesInMinutes * 60);
}


const dateTimeFormat = 'YYYY-MM-DD HH:mm:ss';
const timeFormat1 = 'HH:mm';
const timeFormat2 = 'HH:mm:ss';
